// prettier-ignore
import { ProctorBucketAllocation, buildProctorBucketAllocation } from '../../../shared/proctor/ProctorBucketAllocation';
import { ProctorAllocation } from '../../../shared/native-modules/types/JSTProctorBridge';
import { applyProctorOverride } from '../../../shared/proctor/applyProctorOverride';
import { createContext, useCallback, useContext } from 'react';
import { useJSTProctor } from '@indeed/tare/proctor/JSTProctor';
import { ViewjobProctorSpecification, ViewjobProctorSpecifications } from './ViewjobProctorSpecifications';
import { usePromise } from '../../../shared/util/usePromise';

const ViewjobProctorContext = createContext<Record<string, ProctorAllocation>>({});

export function useLoadViewjobProctorAllocations() {
    const proctor = useJSTProctor();
    const loadAllocations = useCallback(() => proctor.getProctorAllocations(ViewjobProctorSpecifications), [proctor]);
    return usePromise(loadAllocations);
}

export function useAllViewjobProctorAllocations(): Record<string, ProctorAllocation> {
    return useContext(ViewjobProctorContext);
}

export function useViewjobProctorAllocation<Specification extends ViewjobProctorSpecification>(
    test: Specification,
): ProctorBucketAllocation<Specification> {
    const allocations = useContext(ViewjobProctorContext);
    const allocation = allocations[test.name];
    return buildProctorBucketAllocation(test, allocation);
}

/**
 * Provides a React context for the proctor allocations used in this React Native provider
 */
export function ViewjobProctorProvider({
    children,
    allocations,
    override,
}: {
    children: React.ReactNode;
    allocations: Record<string, ProctorAllocation>;
    override?: Record<string, ProctorAllocation> | null | undefined;
}) {
    const mergedAllocations = applyProctorOverride(ViewjobProctorSpecifications, allocations, override);
    return <ViewjobProctorContext.Provider value={mergedAllocations}>{children}</ViewjobProctorContext.Provider>;
}